import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Linkedin, Globe, Send, Loader2, Download } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import apiService from "../services/api";

const Contact = ({ profile }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({ name: "", email: "", subject: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  if (!profile) return null;

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const result = await apiService.submitContactForm(formData);
      if (result.success) {
        toast({ title: "Message transmitted", description: "I will get back to you shortly." });
        setFormData({ name: "", email: "", subject: "", message: "" });
      }
    } catch (error) {
      if (error?.response?.status === 429) {
        toast({ title: "Too many requests", description: "Please wait a minute and try again.", variant: "destructive" });
      } else {
        toast({ title: "Transmission failed", description: "Please try again.", variant: "destructive" });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await apiService.downloadResume();
    } catch (e) {
      toast({ title: "Download failed", description: "Please try again.", variant: "destructive" });
    } finally {
      setDownloading(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-sm text-gray-100 placeholder:text-gray-600 focus:outline-none focus:border-emerald-500 transition-colors";

  return (
    <section id="contact" className="py-24 md:py-32 border-t border-white/10" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <p className="font-mono text-xs tracking-[0.3em] text-emerald-400 mb-4">FINAL CHAPTER — OPEN CHANNEL</p>
        <h2 className="font-display text-3xl md:text-5xl text-gray-100 mb-20">
          Initiate <span className="italic text-emerald-400">secure protocol.</span>
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-gray-400 leading-relaxed mb-12 max-w-md">
              Open to conversations on infrastructure strategy, security architecture,
              cloud transformation — or the next chapter.
            </p>

            <div className="space-y-6 font-mono text-sm">
              <a href={`mailto:${profile.email}`} className="flex items-center gap-4 text-gray-400 hover:text-emerald-400 transition-colors" data-testid="contact-email-link">
                <Mail className="h-4 w-4 text-emerald-500" /> {profile.email}
              </a>
              <a href={`tel:${profile.phone}`} className="flex items-center gap-4 text-gray-400 hover:text-emerald-400 transition-colors" data-testid="contact-phone-link">
                <Phone className="h-4 w-4 text-emerald-500" /> {profile.phone}
              </a>
              <p className="flex items-center gap-4 text-gray-400" data-testid="contact-location">
                <MapPin className="h-4 w-4 text-emerald-500" /> {profile.location}
              </p>
              <a href={profile.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 text-gray-400 hover:text-emerald-400 transition-colors" data-testid="contact-linkedin-link">
                <Linkedin className="h-4 w-4 text-emerald-500" /> LinkedIn Profile
              </a>
              <p className="flex items-center gap-4 text-gray-400" data-testid="contact-website">
                <Globe className="h-4 w-4 text-emerald-500" /> {profile.website}
              </p>
            </div>

            <button
              onClick={handleDownload}
              disabled={downloading}
              className="mt-12 border border-emerald-500/50 text-emerald-400 hover:bg-emerald-500 hover:text-black transition-colors font-mono tracking-wider uppercase text-sm px-6 py-3 inline-flex items-center gap-2 disabled:opacity-60"
              aria-label="Download resume PDF"
              data-testid="contact-download-resume-btn"
            >
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Download Full Resume
            </button>
          </motion.div>

          <motion.form
            onSubmit={handleSubmit}
            className="space-y-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            viewport={{ once: true }}
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div>
                <label htmlFor="name" className="font-mono text-xs tracking-widest uppercase text-gray-500 block mb-1">Name</label>
                <input id="name" name="name" value={formData.name} onChange={handleChange} placeholder="Your name" required aria-label="Your name" className={inputClass} data-testid="contact-input-name" />
              </div>
              <div>
                <label htmlFor="email" className="font-mono text-xs tracking-widest uppercase text-gray-500 block mb-1">Email</label>
                <input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="your@email.com" required aria-label="Your email" className={inputClass} data-testid="contact-input-email" />
              </div>
            </div>
            <div>
              <label htmlFor="subject" className="font-mono text-xs tracking-widest uppercase text-gray-500 block mb-1">Subject</label>
              <input id="subject" name="subject" value={formData.subject} onChange={handleChange} placeholder="What's this about?" required aria-label="Subject" className={inputClass} data-testid="contact-input-subject" />
            </div>
            <div>
              <label htmlFor="message" className="font-mono text-xs tracking-widest uppercase text-gray-500 block mb-1">Message</label>
              <textarea id="message" name="message" value={formData.message} onChange={handleChange} placeholder="Your message..." rows={5} required aria-label="Your message" className={`${inputClass} resize-none`} data-testid="contact-input-message" />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-emerald-500 text-black hover:bg-emerald-400 transition-colors font-mono tracking-wider uppercase text-sm px-8 py-3 inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="contact-submit-btn"
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {isSubmitting ? "Transmitting..." : "Send Message"}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
